import MediaUtils from "lib/MediaUtils";
import { PublicationType } from "models/ApiEnums";
import { ContentMetadataGetDto, FeedItemBestAnswerDto, FeedItemBodyDto, FeedItemPermissionsDto, FileGetDto, FileThumbnaildGetDto } from "models/ApiModels";
import { AttachType } from "models/AppModels";

export type LightBoxProps = {
    images: FileGetDto[];
    thumbnails: FileGetDto[];
};

interface PublicationParams {
    publicationType: PublicationType;
    body: FeedItemBodyDto;
    permissions?: FeedItemPermissionsDto;
    bestAnswer?: FeedItemBestAnswerDto;
}

export class CardBodyData {

    /** Metadados da publicação */
    metadata: ContentMetadataGetDto;

    /** Anexos que devem ser mostrados */
    attachs: FileThumbnaildGetDto[];

    /** Anexos que devem ser mostrados */
    nonEmbedAttachs: FileThumbnaildGetDto[];

    /** Conteúdo a ser mostrado */
    content: string;
    summary: string;

    /** Informa se o texto está expandido. São três valores possíveis: (true) está expandido, (false) está retraído ou (null) está expandido e não pode ser contraído. */
    textExpanded: boolean;

    /** Imagem definida na publicação. Veja o método pictureUrl(). */
    imageDataOrUrl: string;

    private publicationType: PublicationType;

    /**
     *  Construtor 
     * @param body
     * @param ignoreBestAnswer
     * @param forceFullContent
     */
    constructor(pub: PublicationParams, ignoreBestAnswer: boolean, forceFullContent: boolean) {
        const body = (pub.bestAnswer != null && !ignoreBestAnswer) ? pub.bestAnswer.body : pub.body;
        const hasAccess = pub.permissions?.hasAccess ?? true;

        this.publicationType = pub.publicationType;
        this.textExpanded = hasAccess ? body.fullContentLoaded : null;
        this.metadata = hasAccess ? body.metadata : null;
        this.attachs = hasAccess ? body.attachs : [];
        this.nonEmbedAttachs = this.attachs.filter(p => !p.mainFile.isEmbed);
        this.imageDataOrUrl = hasAccess ? body.imageDataOrUrl : null;
        this.summary = hasAccess ? body.summary : null;
        this.content = !hasAccess
            ? null
            : this.textExpanded == null || this.textExpanded === true || forceFullContent
                ? body.fullContent
                : body.summary;
    }

    /** Obtém o tipo de anexo que está vinculado à publicação */
    public typeOfAttach(): AttachType {
        if (this.nonEmbedAttachs && this.nonEmbedAttachs.length > 0) {
            return MediaUtils.getAttachTypeFromMimeType(this.nonEmbedAttachs[0].mainFile.mimeType);
        }
        return null;
    }

    /** Contém uma única imagem ? */
    public containsSingleImage(): boolean {
        var contentHasImage = this.content != null && this.content.indexOf("<img") > 1;
        return (!contentHasImage && this.nonEmbedAttachs && this.nonEmbedAttachs.length == 1 && MediaUtils.getAttachTypeFromMimeType(this.nonEmbedAttachs[0].mainFile.mimeType) == AttachType.Image)
            ||
            (this.imageDataOrUrl != null && this.imageDataOrUrl.indexOf("data:") > -1);
    }

    /** Contém um album de imagens? */
    public isImageAlbum(): boolean {
        return this.nonEmbedAttachs && this.nonEmbedAttachs.length > 1 && MediaUtils.getAttachTypeFromMimeType(this.nonEmbedAttachs[0].mainFile.mimeType) == AttachType.Image;
    }

    /** Contém um vídeo? */
    public isVideo(): boolean {
        return this.nonEmbedAttachs && this.nonEmbedAttachs.length > 0 && MediaUtils.getAttachTypeFromMimeType(this.nonEmbedAttachs[0].mainFile.mimeType) == AttachType.Video;
    }

    /** Determina a imagem que deve ser exibida no corpo da publicação */
    public getPictureUrl() {
        var img: FileThumbnaildGetDto = null;

        if (this.attachs && this.attachs.length > 0) {
            if (this.imageDataOrUrl != null) {
                const url = this.imageDataOrUrl.toLowerCase();
                let f = this.attachs.find(p => p.mainFile.url == url);
                if (f == null) {
                    f = this.attachs.find(p => p.thumbnail && p.thumbnail.url == url);
                    if (f != null) img = f;
                }
                else img = f;
            }

            if (img == null) {
                img = this.attachs.find(p => MediaUtils.getAttachTypeFromMimeType(p.mainFile.mimeType) == AttachType.Image || MediaUtils.getAttachTypeFromMimeType(p.mainFile.mimeType) == AttachType.Video);
            }
        }

        return img;
    }


    public getContentLightBoxData(): LightBoxProps {

        if (this.attachs && this.attachs.length > 1 && this.publicationType != PublicationType.List) {
            const imageList = this.attachs.filter(p => MediaUtils.getAttachTypeFromMimeType(p.mainFile.mimeType) == AttachType.Image && p.mainFile.isEmbed);
            return {
                images: imageList.map(v => v.mainFile),
                thumbnails: imageList.map(v => v.thumbnail ?? v.mainFile)
            };
        }
        return null;
    }

    /** Verifica se o contéudo contém SOMENTE um link. */
    public isLinkOnly(): boolean {
        return ((this.content == null || this.content == "") && this.metadata != null && this.metadata.url != null)
            || (this.summary != null && this.metadata != null && this.metadata.url != null && this.summary.indexOf(this.metadata.url) == 0);
    }


    public emptyContent(): boolean {
        return (this.content == null || this.content == "") && (this.metadata == null || this.metadata.url == null);
    }

    public hasAttach(): boolean {
        return (this.nonEmbedAttachs.length > 0 && this.publicationType != PublicationType.List) || (this.metadata != null && this.metadata.url != null);
    }

    public hasLink(): boolean {
        /** Conteúdo do anexo de arquivo ou link */
        const fileOrLink: FileThumbnaildGetDto[] | ContentMetadataGetDto = this.metadata || this.nonEmbedAttachs;

        return fileOrLink.hasOwnProperty("provider") && this.metadata.provider == "website";
    }

    public hasFile(): boolean {
        /** Conteúdo do anexo de arquivo ou link */
        const fileOrLink: FileThumbnaildGetDto[] | ContentMetadataGetDto = this.metadata || this.nonEmbedAttachs;

        if (!fileOrLink.hasOwnProperty("provider") && this.attachs.length > 0) {
            const attachType = MediaUtils.getAttachTypeFromMimeType(this.attachs[0].mainFile.mimeType);
            return attachType == AttachType.File;
        }
        return false;
    }

}
